import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { API_BASE, loadSession } from '../../api/client';
import './playbookRun.css';

/**
 * A playbook run as it happens: the step pipeline, what is running now with
 * its live measurements, and a feed of what just happened. The run arrives
 * over a WebSocket (…/runs/{id}/stream) the moment it changes; the caller's
 * polling stays underneath as the fallback.
 */

export type RunStep = { id: string; kind: string; name: string; state: string; seconds?: number; hold_seconds?: number; started_at?: string; ended_at?: string; due_at?: string; error?: string; device_id?: string; device_ids?: string[]; sync_all?: boolean; test_id?: string; agent_id?: string; responder_id?: string; ping_target?: string; interface?: string; change?: { admin_up?: boolean; description?: string }; restore?: boolean; background?: boolean; handle?: string; instructions?: string };
export type RunCapture = { device_id: string; phase: string; state: string; error?: string };
export type RunWorkload = { id: string; step_id: string; kind: string; phase: string; state: string; due_at: string; error?: string; result?: unknown };
export type LiveRun = { id: string; revision: number; state: string; current: number; started_at: string; deadline: string; ended_at?: string; error?: string; pause_requested: boolean; steps: RunStep[]; captures: RunCapture[]; workloads: RunWorkload[] };
type Names = { devices: Map<string, string>; agents: Map<string, string> };

const TERMINAL = new Set(['completed', 'failed', 'stopped', 'cancelled', 'expired']);
const DONE = new Set(['completed', 'skipped']);
const BAD = new Set(['failed', 'expired', 'refused']);
const active = (r: LiveRun) => ['capturing', 'running', 'paused', 'finalizing'].includes(r.state);
const stepClass = (s: string) => DONE.has(s) ? 'completed' : BAD.has(s) ? 'failed' : s === 'cancelled' ? 'cancelled' : s === 'paused' || s === 'needs_review' ? 'paused' : s === 'queued' || s === 'pending' ? 'queued' : 'running';
const fmt = (ms: number) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const clock = (d: Date) => d.toTimeString().slice(0, 8);
const words = (s: string) => s.replaceAll('_', ' ');

/** The run, live over WebSocket when the browser can; else whatever the caller polls. */
/**
 * The run with every list present. The server sends a list nothing has been
 * added to yet as null (no workloads until the first measurement starts), and
 * the views read them without checking.
 */
export function complete<T extends LiveRun>(r: T): T {
  return r.steps && r.captures && r.workloads ? r : { ...r, steps: r.steps ?? [], captures: r.captures ?? [], workloads: r.workloads ?? [] };
}

export function useLiveRun<T extends LiveRun>(base: string, polled: T): { run: T; live: boolean } {
  const [streamed, setStreamed] = useState<T | null>(null);
  const [live, setLive] = useState(false);
  const finished = !active(polled);
  useEffect(() => {
    if (typeof WebSocket === 'undefined' || finished) return;
    const loc = window.location, scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    const q = new URLSearchParams({ tenant: loadSession()?.tenantId ?? '' });
    let ws: WebSocket | null = null, closed = false, retry: number | undefined, tries = 0;
    const open = () => {
      ws = new WebSocket(`${scheme}//${loc.host}${API_BASE}${base}/runs/${polled.id}/stream?${q}`);
      ws.onopen = () => { tries = 0; setLive(true); };
      ws.onmessage = e => { try { const f = JSON.parse(String(e.data)); if (f.type === 'run' && f.run) setStreamed(f.run); } catch { /* a bad frame is skipped; the next one replaces it */ } };
      ws.onclose = () => { setLive(false); if (!closed && tries++ < 20) retry = window.setTimeout(open, Math.min(8000, 500 * 2 ** tries)); };
    };
    open();
    return () => { closed = true; window.clearTimeout(retry); ws?.close(); };
  }, [base, polled.id, finished]);
  // Whichever is newer: a control action's reply arrives by polling first.
  const chosen = streamed && streamed.id === polled.id && streamed.revision >= polled.revision ? streamed : polled;
  const run = useMemo(() => complete(chosen), [chosen]);
  return { run, live };
}

/** A pipeline item: a real step, or the starting and final evidence the run collects around them. */
type Item = { key: string; name: string; state: string; desc: string; kind: string; step?: RunStep; devices?: RunCapture[] };

export function items(r: LiveRun, describe: (s: RunStep) => string): Item[] {
  const phase = (p: string) => r.captures.filter(c => c.phase === p);
  const capState = (cs: RunCapture[], started: boolean) => !cs.length ? (started ? 'completed' : 'queued') : cs.some(c => BAD.has(c.state)) ? 'failed' : cs.every(c => c.state === 'completed') ? 'completed' : 'running';
  const out: Item[] = [];
  const start = phase('starting');
  if (start.length || r.state === 'capturing') out.push({ key: 'starting', name: 'Capture starting state', kind: 'capture', state: r.state === 'capturing' ? capState(start, false) === 'completed' ? 'running' : capState(start, false) : capState(start, true), desc: 'Every device the playbook touches, before anything changes', devices: start });
  for (const s of r.steps) out.push({ key: s.id, name: s.name, state: s.state, kind: s.kind, desc: describe(s), step: s, devices: phase(`step_${s.id}`) });
  const final = phase('final');
  if (final.length || r.state === 'finalizing' || TERMINAL.has(r.state)) out.push({ key: 'final', name: 'Final evidence and comparison', kind: 'capture', state: r.state === 'finalizing' ? 'running' : final.length ? capState(final, true) : TERMINAL.has(r.state) ? 'completed' : 'queued', desc: 'Compare final with starting state', devices: final });
  return out;
}

const ICON: Record<string, ReactNode> = {
  capture: <path d="M3 4h10v8H3zM5 7h6M5 10h4" />,
  sync: <path d="M13 6a5 5 0 0 0-9-1M3 10a5 5 0 0 0 9 1M4 2v3h3M12 14v-3H9" />,
  test: <path d="M1 9h3l2-5 3 9 2-4h4" />,
  traffic: <path d="M2 5h10l-2-2M14 11H4l2 2" />,
  interface: <path d="M2 5h12v7H2zM5 8h1M8 8h1M11 8h1" />,
  snippet: <path d="M5 4L2 8l3 4M11 4l3 4-3 4" />,
  wait: <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2zM8 5v3l2 2" />,
  manual: <path d="M4 3h8v10H4zM6 6h4M6 9h3" />,
  stop: <path d="M4 4h8v8H4z" />,
  ok: <path d="M3.5 8.5l3 3 6-7" />,
  bad: <path d="M4 4l8 8M12 4l-8 8" />,
};
const Icon = ({ k }: { k: string }) => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={k === 'ok' || k === 'bad' ? 2.2 : 1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON[k] ?? ICON.manual}</svg>;

/** Progress of a timed step, 0–1, from when it started to when it is due. */
function progress(s: RunStep | undefined, now: number): number | undefined {
  if (!s?.due_at) return undefined;
  const due = Date.parse(s.due_at), start = s.started_at ? Date.parse(s.started_at) : due - (s.seconds ?? s.hold_seconds ?? 30) * 1000;
  if (!(due > start)) return undefined;
  return Math.min(1, Math.max(0, (now - start) / (due - start)));
}

/** What happened between two snapshots, as feed lines. */
type FeedLine = { id: number; at: Date; kind: 'run' | 'ok' | 'bad' | 'warn'; text: string };
function changes(prev: LiveRun | null, next: LiveRun, names: Names): Omit<FeedLine, 'id'>[] {
  const out: Omit<FeedLine, 'id'>[] = [], at = new Date();
  const dev = (id: string) => names.devices.get(id) ?? id.slice(0, 8);
  if (!prev) {
    out.push({ at: new Date(next.started_at), kind: 'run', text: `Run started · ${next.steps.length} steps` });
    for (const s of next.steps) {
      if (s.started_at) out.push({ at: new Date(s.started_at), kind: 'run', text: `${s.name} started` });
      if (s.ended_at && DONE.has(s.state)) out.push({ at: new Date(s.ended_at), kind: 'ok', text: `${s.name} · ${words(s.state)}` });
      if (s.ended_at && BAD.has(s.state)) out.push({ at: new Date(s.ended_at), kind: 'bad', text: `${s.name} · ${words(s.state)}${s.error ? ` · ${words(s.error)}` : ''}` });
    }
    return out.sort((a, b) => a.at.getTime() - b.at.getTime());
  }
  if (prev.state !== next.state) out.push({ at, kind: next.state === 'failed' ? 'bad' : next.state === 'completed' ? 'ok' : next.state === 'paused' || next.state === 'finalizing' ? 'warn' : 'run', text: next.state === 'finalizing' ? 'Collecting final evidence' : `Run ${words(next.state)}${next.error ? ` · ${words(next.error)}` : ''}` });
  const before = new Map(prev.steps.map(s => [s.id, s]));
  for (const s of next.steps) {
    const was = before.get(s.id)?.state;
    if (was === s.state) continue;
    const c = stepClass(s.state);
    out.push({ at, kind: c === 'completed' ? 'ok' : c === 'failed' ? 'bad' : c === 'cancelled' || c === 'paused' ? 'warn' : 'run', text: c === 'running' ? `${s.name} started` : `${s.name} · ${words(s.state)}${s.error ? ` · ${words(s.error)}` : ''}` });
  }
  const capKey = (c: RunCapture) => `${c.phase}/${c.device_id}`, caps = new Map(prev.captures.map(c => [capKey(c), c.state]));
  for (const c of next.captures) {
    if (caps.get(capKey(c)) === c.state) continue;
    if (c.state === 'completed') out.push({ at, kind: 'ok', text: `${dev(c.device_id)} · ${c.phase === 'starting' ? 'starting' : c.phase === 'final' ? 'final' : 'current'} state collected` });
    else if (BAD.has(c.state)) out.push({ at, kind: 'bad', text: `${dev(c.device_id)} · collection ${words(c.state)}${c.error ? ` · ${words(c.error)}` : ''}` });
  }
  const loads = new Map(prev.workloads.map(w => [w.id, w.state]));
  for (const w of next.workloads) {
    if (loads.get(w.id) === w.state) continue;
    if (w.state === 'complete') out.push({ at, kind: 'ok', text: `${next.steps.find(s => s.id === w.step_id)?.name ?? w.kind} · ${w.phase === 'step' ? '' : `${w.phase} `}measurement complete` });
    else if (w.state === 'failed') out.push({ at, kind: 'bad', text: `${w.kind} measurement failed${w.error ? ` · ${words(w.error)}` : ''}` });
  }
  return out;
}

/** Live samples for the running step: RTT for a test, throughput for traffic. */
type Sample = { v: number; lost?: boolean };
function useLiveSamples(step: RunStep | undefined, workload: RunWorkload | undefined, running: boolean): Sample[] {
  // Each step keeps what was drawn for it while this page was open, so a
  // short step's chart stays after it finishes, and returning to a step
  // shows its series again.
  const kept = useRef(new Map<string, Sample[]>()), totals = useRef(new Map<string, { received: number; sum: number; lost: number }>());
  const id = step?.id ?? '';
  const [samples, setSamples] = useState<Sample[]>([]);
  useEffect(() => { setSamples(kept.current.get(id) ?? []); }, [id]);
  const add = (key: string, more: Sample[]) => {
    const next = [...(kept.current.get(key) ?? []), ...more].slice(-200);
    kept.current.set(key, next);
    setSamples(next);
  };
  // Traffic reports progress on its session stream while it runs.
  const traffic = running && step?.kind === 'traffic' && workload ? workload.id : '';
  useEffect(() => {
    if (!traffic || typeof WebSocket === 'undefined') return;
    const loc = window.location, scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:', tenant = loadSession()?.tenantId ?? '';
    const ws = new WebSocket(`${scheme}//${loc.host}${API_BASE}/stream?${new URLSearchParams({ topics: 'session', session_id: traffic, tenant })}`);
    const key = id;
    ws.onmessage = e => {
      try {
        const m = JSON.parse(String(e.data));
        if (m.kind === 'progress') add(key, [{ v: Math.max(m.tx_mbps ?? 0, m.rx_mbps ?? 0) }]);
      } catch { /* skip */ }
    };
    return () => ws.close();
  }, [traffic]);
  // A playbook test runs as an instance, which reports running totals (probes,
  // losses, mean RTT) on the run rather than each probe. The probes since the
  // last report are what changed: their mean RTT, and how many were lost.
  const result = workload?.result as { samples?: number; lost?: number; average_ms?: number } | undefined;
  useEffect(() => {
    if (!id || step?.kind !== 'test' || typeof result?.samples !== 'number') return;
    const lost = result.lost ?? 0, received = result.samples - lost, sum = (result.average_ms ?? 0) * received;
    const before = totals.current.get(id) ?? { received: 0, sum: 0, lost: 0 }, more: Sample[] = [];
    for (let i = 0; i < Math.min(50, lost - before.lost); i++) more.push({ v: NaN, lost: true });
    if (received > before.received) more.push({ v: (sum - before.sum) / (received - before.received) });
    totals.current.set(id, { received, sum, lost });
    if (more.length) add(id, more);
  }, [id, step?.kind, result?.samples, result?.lost, result?.average_ms]);
  return samples;
}

function Spark({ samples, unit, state }: { samples: Sample[]; unit: string; state: string }) {
  const W = 400, H = 110, pad = { l: 30, r: 8, t: 8, b: 16 };
  const vals = samples.map(s => s.v).filter(Number.isFinite);
  const max = Math.max(unit === 'ms' ? 10 : 1, ...vals) * 1.15;
  const X = (i: number) => pad.l + (i / 199) * (W - pad.l - pad.r), Y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const pts = samples.map((s, i) => Number.isFinite(s.v) ? `${X(i).toFixed(1)},${Y(s.v).toFixed(1)}` : null).filter(Boolean) as string[];
  const last = samples.length ? samples[samples.length - 1] : undefined;
  return <svg className={`pbr-spark ${state}`} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Live ${unit === 'ms' ? 'round-trip time' : 'throughput'}, ${samples.length} samples`}>
    {[0, max / 2, max].map(t => <g key={t}><line x1={pad.l} x2={W - pad.r} y1={Y(t)} y2={Y(t)} className="grid" /><text x={2} y={Y(t) + 3} className="tick">{t < 10 ? t.toFixed(1) : t.toFixed(0)}</text></g>)}
    <text x={pad.l + 4} y={H - 3} className="tick">{unit}</text>
    {pts.length > 1 && <><polygon className="area" points={`${pts[0]!.split(',')[0]},${Y(0)} ${pts.join(' ')} ${pts[pts.length - 1]!.split(',')[0]},${Y(0)}`} /><polyline className="line" points={pts.join(' ')} /></>}
    {samples.map((s, i) => s.lost ? <line key={i} x1={X(i)} x2={X(i)} y1={pad.t} y2={H - pad.b} className="lost" /> : null)}
    {last && Number.isFinite(last.v) && <circle cx={X(samples.length - 1)} cy={Y(last.v)} r={3.5} className="dot" />}
  </svg>;
}

export function PlaybookRunView({ run: given, names, describe, controls, live, after }: { run: LiveRun; names: Names; describe: (s: RunStep) => string; controls: ReactNode; live: boolean; after?: ReactNode }) {
  const r = useMemo(() => complete(given), [given]);
  const [now, setNow] = useState(Date.now());
  const [selected, setSelected] = useState<string | null>(null);
  // Ticks smoothly while something is timed; once a second otherwise.
  useEffect(() => {
    if (!active(r)) return;
    let raf = 0, last = 0;
    const loop = (t: number) => { if (t - last > 100) { last = t; setNow(Date.now()); } raf = requestAnimationFrame(loop); };
    if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(loop);
    else { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }
    return () => cancelAnimationFrame(raf);
  }, [r.state]);

  // The feed is built from what changed between the snapshots this page saw.
  const prev = useRef<LiveRun | null>(null), seq = useRef(0);
  const [feed, setFeed] = useState<FeedLine[]>([]);
  useEffect(() => {
    if (prev.current && prev.current.revision === r.revision && prev.current.id === r.id) return;
    const lines = changes(prev.current?.id === r.id ? prev.current : null, r, names).map(l => ({ ...l, id: ++seq.current }));
    if (prev.current?.id !== r.id) setFeed(lines.reverse());
    else if (lines.length) setFeed(f => [...lines.reverse(), ...f].slice(0, 200));
    prev.current = r;
  }, [r, names]);

  const list = useMemo(() => items(r, describe), [r, describe]);
  const runningItem = list.find(i => stepClass(i.state) === 'running');
  const lastItem = [...list].reverse().find(i => stepClass(i.state) !== 'queued');
  const shown = list.find(i => i.key === selected) ?? runningItem ?? lastItem ?? list[0];
  const shownStep = shown?.step;
  const shownLoad = shownStep ? r.workloads.find(w => w.step_id === shownStep.id && w.phase === 'step') : undefined;
  const samples = useLiveSamples(shownStep, shownLoad, !!shownStep && stepClass(shownStep.state) === 'running');
  const shownTotals = shownLoad?.result as { samples?: number; lost?: number; average_ms?: number } | undefined;
  const end = r.ended_at ? Date.parse(r.ended_at) : now;
  const terminal = TERMINAL.has(r.state);
  const failedCount = list.filter(i => stepClass(i.state) === 'failed').length;
  const doneCount = list.filter(i => stepClass(i.state) === 'completed').length;
  const cancelledCount = list.filter(i => stepClass(i.state) === 'cancelled').length;
  const position = Math.min(r.current + 1, r.steps.length);
  const status = r.state === 'running' ? `Running · step ${position} of ${r.steps.length}` : r.state === 'capturing' ? 'Recording the starting state' : r.state === 'finalizing' ? 'Collecting final evidence' : words(r.state)[0]!.toUpperCase() + words(r.state).slice(1);
  const p = shownStep ? progress(shownStep, now) : undefined;
  const capDone = shown?.devices?.filter(c => c.state === 'completed').length ?? 0;
  const ringP = shown && stepClass(shown.state) === 'completed' ? 1 : shown?.devices?.length ? capDone / shown.devices.length : p ?? (stepClass(shown?.state ?? '') === 'running' ? 0.08 : 0);
  const ringState = stepClass(shown?.state ?? 'queued');
  const unit = shownStep?.kind === 'traffic' ? 'Mbit/s' : 'ms';
  const valid = samples.map(s => s.v).filter(Number.isFinite);

  return <div className="pbr" aria-label="Execution progress">
    <section className="pbr-head" aria-label="Run status">
      <div className="pbr-headrow">
        <div className={`pbr-status ${r.state}`} role="status"><span className="dot" />{status}{r.pause_requested && r.state === 'running' ? ' · pause requested' : ''}</div>
        <div className="pbr-clocks">
          <div><b>{fmt(end - Date.parse(r.started_at))}</b><span>Elapsed</span></div>
          {!terminal && <div><b>{fmt(Date.parse(r.deadline) - now)}</b><span>Until deadline</span></div>}
          <div title={live ? 'Updates arrive as they happen' : 'Checking every 2 seconds'}><b className={live ? 'live' : ''}>{live ? 'Live' : 'Polling'}</b><span>Updates</span></div>
        </div>
        <div className="pbr-controls">{controls}</div>
      </div>
      <div className="pbr-segments" aria-hidden="true">{list.map(i => { const c = stepClass(i.state), ip = i.step ? progress(i.step, now) : i.devices?.length ? (i.devices.filter(d => d.state === 'completed').length / i.devices.length) : undefined; return <div key={i.key} className={`seg ${c}`}><i style={{ width: `${c === 'completed' || c === 'failed' ? 100 : c === 'running' ? Math.round((ip ?? 0.12) * 100) : 0}%` }} /></div>; })}</div>
      <p className="muted pbr-meta">Started {new Date(r.started_at).toLocaleString()} · Deadline {new Date(r.deadline).toLocaleTimeString()}{r.ended_at ? ` · Ended ${new Date(r.ended_at).toLocaleTimeString()}` : ''}</p>
    </section>

    {terminal && <div className={`pbr-summary ${failedCount || r.state !== 'completed' ? 'bad' : 'ok'}`} role="status">
      <strong>{r.state === 'completed' && !failedCount ? 'Run completed' : `Run ${words(r.state)}${r.error ? `: ${words(r.error)}` : ''}`}</strong>
      <div><b>{doneCount}</b><span>Succeeded</span></div><div><b>{failedCount}</b><span>Failed</span></div><div><b>{cancelledCount}</b><span>Cancelled</span></div><div><b>{fmt(end - Date.parse(r.started_at))}</b><span>Duration</span></div>
    </div>}

    <div className="pbr-grid">
      <section className="pbr-panel" aria-label="Steps">
        <h3>Steps</h3>
        <ol className="pbr-pipeline">
          {list.map((i, n) => { const c = stepClass(i.state); const ip = i.step ? progress(i.step, now) : undefined; return (
            <li key={i.key} className={`pbr-step ${c}${shown?.key === i.key && selected ? ' selected' : ''}`} aria-current={c === 'running' ? 'step' : undefined}>
              <button type="button" className="node" aria-label={`${i.name}: ${words(i.state)}`} aria-pressed={selected === i.key} onClick={() => setSelected(selected === i.key ? null : i.key)}>{c === 'completed' ? <Icon k="ok" /> : c === 'failed' ? <Icon k="bad" /> : c === 'running' ? <Icon k={i.kind} /> : <span>{n + 1}</span>}</button>
              <div className="card" onClick={() => setSelected(selected === i.key ? null : i.key)}>
                <div className="cardhead"><strong>{i.name}</strong><span className="chip">{c === 'running' && i.step?.due_at ? `${Math.max(0, Math.ceil((Date.parse(i.step.due_at) - now) / 1000))} s` : words(i.state)}</span></div>
                <div className="desc">{i.desc}</div>
                {!!i.devices?.length && <div className="devices">{i.devices.map(d => <span key={`${d.phase}/${d.device_id}`} className={`dev ${d.state === 'completed' ? 'done' : BAD.has(d.state) ? 'bad' : d.state === 'queued' ? '' : 'busy'}`} title={d.error ? words(d.error) : words(d.state)}>{names.devices.get(d.device_id) ?? d.device_id.slice(0, 8)}</span>)}</div>}
                {c !== 'queued' && c !== 'cancelled' && <div className="bar"><i style={{ width: `${c === 'running' ? Math.round((ip ?? (i.devices?.length ? i.devices.filter(d => d.state === 'completed').length / i.devices.length : 0.12)) * 100) : 100}%` }} /></div>}
                {i.step?.error && <div className="outcome" role="alert">{words(i.step.error)}</div>}
              </div>
            </li>); })}
        </ol>
      </section>

      <div className="pbr-side">
        <section className="pbr-panel" aria-label="Current step">
          <h3>{selected ? 'Selected step' : runningItem ? 'Now running' : terminal ? 'Last step' : 'Up next'}</h3>
          {shown && <div className="pbr-live">
            <div className={`ring ${ringState}`}>
              <svg viewBox="0 0 116 116" aria-hidden="true"><circle className="track" cx="58" cy="58" r="50" /><circle className="fill" cx="58" cy="58" r="50" strokeDasharray="314.16" strokeDashoffset={314.16 * (1 - ringP)} /></svg>
              <div className="label">{shown.devices?.length ? <><b>{capDone}/{shown.devices.length}</b><span>devices</span></> : ringState === 'running' && shownStep?.due_at ? <><b>{Math.max(0, Math.ceil((Date.parse(shownStep.due_at) - now) / 1000))}s</b><span>left</span></> : <><b>{ringState === 'completed' ? '✓' : ringState === 'failed' ? '✕' : '–'}</b><span>{words(shown.state)}</span></>}</div>
            </div>
            <div className="what"><strong>{shown.name}</strong><span className="muted">{shown.desc}</span>
              {shownStep?.kind === 'manual' && shownStep.instructions && <p>{shownStep.instructions}</p>}
              {shownStep?.error && <p className="err" role="alert">{words(shownStep.error)}</p>}
            </div>
          </div>}
          {(shownStep?.kind === 'test' || shownStep?.kind === 'traffic') && <div className="pbr-chart">
            <div className="metrics">
              <div><b>{valid.length ? `${valid[valid.length - 1]!.toFixed(1)} ${unit}` : '—'}</b><span>Now</span></div>
              <div><b>{shownTotals?.average_ms !== undefined ? `${shownTotals.average_ms.toFixed(1)} ${unit}` : valid.length ? `${(valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(1)} ${unit}` : '—'}</b><span>Average</span></div>
              {shownStep.kind === 'test' ? <div><b>{shownTotals?.samples ? `${(((shownTotals.lost ?? 0) / shownTotals.samples) * 100).toFixed(1)}%` : '—'}</b><span>Loss</span></div> : <div><b>{valid.length ? `${Math.max(...valid).toFixed(1)} ${unit}` : '—'}</b><span>Peak</span></div>}
              <div><b>{shownStep.kind === 'test' ? shownTotals?.samples ?? 0 : samples.length}</b><span>{shownStep.kind === 'test' ? 'Probes' : 'Samples'}</span></div>
            </div>
            <Spark samples={samples} unit={unit} state={ringState} />
            {!samples.length && <p className="muted">{stepClass(shownStep.state) === 'running' ? 'Waiting for the first measurement…' : stepClass(shownStep.state) === 'queued' ? 'Measurements show here while the step runs.' : 'No measurements were drawn while this page was open; the totals above are the step\'s own.'}</p>}
          </div>}
        </section>
        <section className="pbr-panel" aria-label="Event feed">
          <h3>Event feed</h3>
          <ul className="pbr-feed" aria-live="polite">{feed.map(l => <li key={l.id} className={l.kind}><span className="t">{clock(l.at)}</span><span className="k" /><span>{l.text}</span></li>)}</ul>
        </section>
      </div>
    </div>
    {after}
  </div>;
}
